import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { UserAddress } from '../types/user/userAddress';
import { UserClient } from '../types/user/userClient';
import { HttpUserAddress, HttpUserClient } from '../http';

interface UserAddressContextType {
  addresses: UserAddress[];
  active: UserAddress | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UserClientContextType {
  clients: UserClient[];
  active: UserClient | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const UserAddressContext = createContext<UserAddressContextType | undefined>(undefined);
const UserClientContext = createContext<UserClientContextType | undefined>(undefined);

export const useUserAddresses = (enabled: boolean = true): UserAddressContextType => {
  const context = useContext(UserAddressContext);
  if (!context) {
    throw new Error('useUserAddresses must be used within a UserDataProvider');
  }
  
  if (!enabled) {
    return {
      addresses: [],
      active: null,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }
  
  return context;
};

export const useUserClients = (enabled: boolean = true): UserClientContextType => {
  const context = useContext(UserClientContext);
  if (!context) {
    throw new Error('useUserClients must be used within a UserDataProvider');
  }
  
  if (!enabled) {
    return {
      clients: [],
      active: null,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }
  
  return context;
};

interface UserDataProviderProps {
  children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [activeAddress, setActiveAddress] = useState<UserAddress | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [errorAddresses, setErrorAddresses] = useState<string | null>(null);

  const [clients, setClients] = useState<UserClient[]>([]);
  const [activeClient, setActiveClient] = useState<UserClient | null>(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [errorClients, setErrorClients] = useState<string | null>(null);

  const loadAddresses = useCallback(async () => {
    try {
      setLoadingAddresses(true);
      setErrorAddresses(null);
      
      const data = await HttpUserAddress.getAllAddresses();
      setAddresses(data || []);
      
      const active = data?.find(addr => addr.activa) || data?.[0] || null;
      setActiveAddress(active);
    } catch (err) {
      console.error('Error loading addresses:', err);
      setErrorAddresses('Error al cargar direcciones');
      setAddresses([]);
      setActiveAddress(null);
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      setErrorClients(null);
      
      const data = await HttpUserClient.getAllClients();
      setClients(data || []);
      
      const active = data?.find(client => client.activo) || data?.[0] || null;
      setActiveClient(active);
    } catch (err) {
      console.error('Error loading clients:', err);
      setErrorClients('Error al cargar clientes');
      setClients([]);
      setActiveClient(null);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const refreshAddresses = useCallback(async () => {
    await loadAddresses();
  }, [loadAddresses]);

  const refreshClients = useCallback(async () => {
    await loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadAddresses().catch(() => {});
    loadClients().catch(() => {});
  }, [loadAddresses, loadClients]);

  const addressContextValue: UserAddressContextType = {
    addresses,
    active: activeAddress,
    loading: loadingAddresses,
    error: errorAddresses,
    refresh: refreshAddresses,
  };

  const clientContextValue: UserClientContextType = {
    clients,
    active: activeClient,
    loading: loadingClients,
    error: errorClients,
    refresh: refreshClients,
  };

  return (
    <UserAddressContext.Provider value={addressContextValue}>
      <UserClientContext.Provider value={clientContextValue}>
        {children}
      </UserClientContext.Provider>
    </UserAddressContext.Provider>
  );
};

